import {
  ContentRegistrationType,
  RecordOrigin,
  UserInfo,
  XMLGenerationStrategy,
} from '@/common/types';
import { FormData } from '@content-registration/types';
import { chapterReferencesRemoved } from '@content-registration/utils/resourceFieldSnapshot';
import { Book, BookMetadata } from '@generated/form-types/book';
import {
  createCrossrefDocument,
  appendElement,
  appendElementIf,
  serializeXml,
} from '../xml-helpers';
import {
  buildContributors,
  buildTitles,
  buildPublicationDates,
  buildIsbns,
  buildDoiData,
  buildSimilarityCheck,
  buildAbstracts,
  buildHead,
  buildPublisher,
  buildSeriesMetadata,
  buildCitationList,
} from '../builders';
import { buildChapter } from './builders';

/**
 * Appends book-level metadata fields to a container element
 * (`<book_metadata>` or `<book_series_metadata>`).
 *
 * XSD element order: contributors, titles, abstract, edition_number,
 * publication_date, isbn/noisbn, publisher, doi_data, citation_list
 */
function buildBookFields(
  container: Element,
  meta: BookMetadata,
  removed: { simCheckUrl: boolean; references: boolean }
): void {
  // 1. contributors
  buildContributors(container, meta.contributors);

  // 2. titles
  buildTitles(container, meta.title, meta.subtitle);

  // 3. abstracts (jats:abstract)
  buildAbstracts(container, meta.abstracts);

  // 4. edition_number
  appendElementIf(container, 'edition_number', meta.editionNumber);

  // 5. publication_date
  buildPublicationDates(container, meta.publicationDates);

  // 6. isbn / noisbn (XSD requires one or the other)
  buildIsbns(container, meta.isbn, 'monograph');

  // 7. publisher
  buildPublisher(container, meta.publisher);

  // 8. doi_data
  const doiData = buildDoiData(container, meta.doi, meta.landingPageUrl);
  buildSimilarityCheck(
    doiData,
    removed.simCheckUrl ? undefined : meta.similarityCheckUrl,
    removed.simCheckUrl
  );

  // 9. citation_list
  buildCitationList(container, meta.references, removed.references);
}

export class BookXmlRenderingStrategy implements XMLGenerationStrategy {
  async generateXML(
    recordType: ContentRegistrationType,
    data: FormData,
    prettyPrint = false,
    userInfo?: UserInfo,
    origin?: RecordOrigin
  ): Promise<string> {
    const bookData = data as unknown as Book;
    const meta = bookData.bookMetadata;
    const hadData = origin?.resourceFieldsImported;
    const removed = {
      simCheckUrl: hadData?.simCheckUrl === true && !meta.similarityCheckUrl?.trim(),
      references: hadData?.references === true && !meta.references?.trim(),
    };

    const doc = createCrossrefDocument();
    const root = doc.documentElement;

    // <head>
    buildHead(root, userInfo);

    // <body>
    const body = appendElement(root, 'body');
    const book = appendElement(body, 'book', null, {
      book_type: meta.bookType,
    });

    const languageAttrs = meta.language
      ? { language: meta.language }
      : undefined;

    if (bookData.series) {
      // <book_series_metadata> — wraps series_metadata + book-level fields
      const bookSeriesMeta = appendElement(
        book,
        'book_series_metadata',
        null,
        languageAttrs
      );
      buildSeriesMetadata(bookSeriesMeta, bookData.series);
      buildBookFields(bookSeriesMeta, meta, removed);
    } else {
      // <book_metadata>
      const bookMetadata = appendElement(
        book,
        'book_metadata',
        null,
        languageAttrs
      );
      buildBookFields(bookMetadata, meta, removed);
    }

    // Chapters — <content_item component_type="chapter">
    if (bookData.chapters) {
      for (const chapter of bookData.chapters) {
        buildChapter(
          book,
          chapter,
          chapterReferencesRemoved(chapter, hadData?.chapters)
        );
      }
    }

    return serializeXml(doc, prettyPrint);
  }
}
